import capitalize from '../services/mini/capitalize';
import getSessionId from '../services/mini/getSessionId';
import * as _ from 'lodash';
import { mapUserApiToModel } from './mappers/mapUserApiToModel';
import { projectCall, call } from './helpers/tutorCalls';

function fillEncGithubTokenInParam(param) {
    if (!param.encGithubToken) {
        delete param.encGithubToken;
    }
    return param;
}

const Api = {

    signIn: function (email, password, encGithubToken) {
        return call('signIn', fillEncGithubTokenInParam({ email, password, encGithubToken }));
    },

    signUp: function (email, password, baseUrl, meta, invitationCode) {
        return call('signUp', { email, password, baseUrl, meta, invitationCode });
    },

    authorize: function ({ email, token }) {
        return call('authorize', { email, token });
    },

    verifySignUp: function (hex, password) {
        return call('verifySignUp', { hex, password });
    },

    getLastVerificationEmail: function (email) {
        return call('getLastVerificationEmail', { email });
    },

    getGoogleOauthUrl: function (redirectUrl) {
        return call('getGoogleOauthUrl', { redirectUrl });
    },

    googleSignIn: function (code, redirectUrl, meta, encGithubToken, invitationCode?) {
        return call('oauthSignIn', fillEncGithubTokenInParam({ code, redirectUrl, meta, encGithubToken, invitationCode }));
    },

    getGithubOauthUrl: function (redirectUrl, repositoryAccess) {
        return call('getGithubOauthUrl', { redirectUrl, repositoryAccess }, 'url');
    },

    getGithubOauthUrlForUser: function (redirectUrl, repositoryAccess) {
        return call('getGithubOauthUrlForUser', { redirectUrl, repositoryAccess }, 'url');
    },

    githubSignIn: function (code, meta, invitationCode?) {
        return call('githubSignIn', { code, meta, invitationCode });
    },

    assignGithubAccount: function (code, repositoryAccess) {
        return call('assignGithubAccount', { code, repositoryAccess });
    },

    getGithubTokenByCode: function (code) {
        return call('getGithubTokenByCode', { code });
    },

    updateGithubToken: function (encGithubToken) {
        return call('updateGithubToken', { encGithubToken });
    },

    revokeGithubAccess: function (encGithubToken) {
        return call('revokeGitHubAccess', { encGithubToken });
    },

    reassignGithubAccount: function (encGithubToken) {
        return call('reassignGithubAccount', { encGithubToken });
    },

    saveProject: function (name, sluVersion) {
        return call('createProject', { name, sluVersion });
    },

    deleteProject: function (projectId) {
        return call('deleteProject', { projectId });
    },

    renameProject: function (projectId, name) {
        return call('renameProject', { projectId, name });
    },

    getProjects: function () {
        return call('getProjects', {}, 'projects');
    },

    getPredefinedProjects: async function (sluVersion) {
        return call('getSampleProjects', { sluVersion }, 'projects');
    },

    getTransformFiles: async function (param) {
        return call('getTransformFiles', param);
    },

    runCodeGenerator: async function (param) {
        return projectCall('generateCode', param);
    },

    reorderProjects: function (projectIds) {
        return call('sortProjects', { projectIds });
    },

    getProject: function (projectId) {
        return call('getProject', { projectId });
    },

    setStageProjectTag: function (projectId, tagId) {
        return call('setStageProjectTag', { projectId, tagId });
    },

    setProdProjectTag: function (projectId, tagId) {
        return call('setProdProjectTag', { projectId, tagId });
    },

    setTestingProjectTag: function (projectId, tagId) {
        return call('setTestingProjectTag', { projectId, tagId });
    },

    createScript: function (projectId, name, text, type) {
        return call('createScript', { projectId, name, text, type });
    },

    updateScript: function (scriptId, text, updateTs, apply) {
        return call('updateScript', { scriptId, text, updateTs, apply }, 'script');
    },

    deleteScript: function (scriptId) {
        return call('deleteScript', { scriptId });
    },

    renameScript: function (scriptId, name) {
        return call('renameScript', { scriptId, name });
    },

    getScripts: function (projectId, tagId) {
        return new Promise(function (resolve, reject) {
            window.tutor.call('getScripts', { projectId, tagId }, function (e, res) {
                if (e) {
                    reject({ error: e });
                } else {
                    resolve(res);
                }
            });
        });
    },

    reorderScripts: function (projectId, scriptIds) {
        return call('sortScripts', { projectId, scriptIds });
    },

    createProjectTag: function (projectId, tagName) {
        return call('createProjectTag', { projectId, message: tagName });
    },

    renameProjectTag: function (projectId, tagId, tagName) {
        return call('renameTag', { projectId, tagId, tagName });
    },

    deleteProjectTag: function (tagId) {
        return call('removeProjectTag', { tagId });
    },

    makeScriptsEditable: function (projectId, tagId) {
        return call('makeScriptsEditable', { projectId, tagId });
    },

    sendText: function (param) {
        return projectCall('text', param);
    },

    reloadProject: function () {
        return projectCall('reload', {});
    },

    resetProject: function () {
        return projectCall('reset', {});
    },

    getProfile: function () {
        return new Promise(function (resolve, reject) {
            window.tutor.call('getProfile', {}, function (e, res) {
                if (e) {
                    reject({ error: e });
                } else if (res && res.error) {
                    reject(res);
                } else {
                    delete res.githubLogin;
                    resolve(mapUserApiToModel(res));
                }
            });
        });
    },

    getGithubLoginName: function (userToken, encGithubToken) {
        return call('getGithubLoginName', { userToken, encGithubToken });
    },

    updateProfile: function (firstName, lastName, companyName) {
        return call('updateProfile', { firstName, lastName, companyName });
    },

    changePassword: function (password, oldPassword, encGithubToken) {
        return call('changePassword', fillEncGithubTokenInParam({ password, oldPassword, encGithubToken }));
    },

    sendPasswordRecoveryEmail: function (email, baseUrl) {
        return call('sendPasswordRecoveryEmail', { email, baseUrl });
    },

    passwordRecovery: function (password, hex) {
        return call('passwordRecovery', { password, hex });
    },

    passwordRecoveryAvailable: function (hex) {
        return call('passwordRecoveryAvailable', { hex });
    },

    getPredefinedScripts: function (sluVersion) {
        return call('getPredefinedScriptList', { sluVersion }, 'scripts');
    },

    getScriptsContent: function (scriptNames) {
        return call('getScriptsContent', { names: scriptNames }, 'scripts');
    },

    setProjectOptions: function (projectId, options, environment) {
        return call(`set${capitalize(environment)}Options`, { projectId, options });
    },

    exportLogs: function (param) {
        return call('exportLogs', param);
    },

    exportDialogs: function (param) {
        return call('exportUserDialogs', param);
    },

    readLogs: function (projectId, level, deployment, file, scriptTagId, dialogId, lastTs, limit, offset, scriptsHash) {
        var param = {
            projectId,
            level,
            deployment,
            file,
            dialogId,
            offset,
            limit
        } as any;

        if (lastTs !== null) {
            param.lastTs = lastTs;
        }

        return call('readLogs', param, 'messages');
    },

    readPhrases: function ({ projectId, deployment, file, scriptTagId, dialogId, dialogIds, line, type, typeSet, notFound, lastTs, fromTs, toTs, limit, offset, scriptsHash, filters }) {
        var param = {
            projectId,
            deployment,
            file,
            line,
            type,
            typeSet,
            offset,
            limit,
            filters,
            fromTs,
            toTs
        } as any;

        if (notFound) {
            param.notFound = notFound;
        }

        if (lastTs !== null) {
            param.lastTs = lastTs;
        }

        if (!_.isEmpty(dialogId)) {
            param.dialogId = dialogId;
        }
        if (!_.isEmpty(dialogIds)) {
            param.dialogIds = dialogIds;
        }

        return call('readPhrase', param, 'phrases');
    },

    getLogImage: function (projectId, requestId, date, deploy) {
        return call('getImg', { projectId, requestId, date, deploy }, 'image');
    },

    getProjectStatisticsByRange: function (projectId, deploy, fromTs, toTs, historyGranularityMs, showTopLocations) {
        return call('getStatistics', { projectId, deploy, fromTs, toTs, showTopLocations: showTopLocations !== undefined ? showTopLocations : false, historyGranularityMs: historyGranularityMs }, null);
    },

    getProjectStatisticsForAllEnvironmentsByRange: function (projectId, fromTs, toTs, historyGranularityMs, showTopLocations) {
        return call('getStatisticsAllDeploys', { projectId, fromTs, toTs, showTopLocations: showTopLocations !== undefined ? showTopLocations : false, historyGranularityMs: historyGranularityMs }, 'result');
    },

    getDeviceStatMinMaxTs: function (projectId, deploy) {
        return call('getDeviceStatMinMaxTs', { projectId, deployment: deploy });
    },

    getProjectLocationStatisticsByDates: function (projectId, deploy, fromTs, toTs) {
        return call('getLocationStatistics', { projectId, deployment: deploy, fromTs, toTs });
    },

    getProjectUsersAndDialogsStatisticsByDates: function (projectId, deploy, fromTs, toTs, limit) {
        return call('getProjectUsers', { projectId, deployment: deploy, fromTs, toTs, limit });
    },

    getPatternStatisticsByDates: function (projectId, deploy, fromTs, toTs, dialogIds) {
        return call('getPatternStatistics', { projectId, deployment: deploy, fromTs, toTs, dialogIds });
    },

    getPatternFlowStatisticsByDates: function ({ projectId, deploy, fromTs, toTs, dialogIds, onlyPatterns }) {
        return call('getPatternFlowStatistics', { projectId, deploy, fromTs, toTs, dialogIds, onlyPatterns, limit: 30 });
    },

    loadAnalyticsReportsMetaData: function (projectId) {
        const method = 'getAnalyticsReportMetaData';
        var promise = new Promise(function (resolve, reject) {
            window.tutor.call(method, { projectId }, function (e, res) {
                if (e) {
                    reject({ error: e, apiCall: method });
                } else if (res && res.error) {
                    reject({ ...res, apiCall: method });
                } else {
                    let settings = {}, reports = [];
                    if (res.analyticsReportMetaData) {
                        const analyticsReportMetaData = JSON.parse(res.analyticsReportMetaData);
                        settings = analyticsReportMetaData.analyticsColumnsSettings;
                        if (analyticsReportMetaData.analyticsColumns) {
                            analyticsReportMetaData.analyticsColumns = analyticsReportMetaData.analyticsColumns.map(col => {
                                return {
                                    name: col.name,
                                    filters: [...col.filters.slice(3)],
                                    period: col.filters[0].value.value,
                                    startTs: col.filters[1].value.startTs,
                                    endTs: col.filters[1].value.endTs,
                                    onlyPatternOnPatternFlow: col.filters[2] ? col.filters[2].value.value : true
                                };
                            });
                        }
                        reports = analyticsReportMetaData.analyticsColumns;
                    }
                    resolve({ settings, reports });
                }
            });
        });

        return promise;
    },

    saveAnalyticsReportMetaData: function (projectId, reports, settings) {
        const analyticsColumns = reports.map(report => {
            return {
                name: report.name,
                filters: getFiltersFromReport(report)
            };
        });

        const analyticsReportsMetaData = {
            analyticsColumns,
            analyticsColumnsSettings: settings,
            updateTs: Date.now()
        };

        return call('saveAnalyticsReportMetaData', {
            projectId,
            analyticsReportMetaData: JSON.stringify(analyticsReportsMetaData)
        });
    },

    getTestConversations: function (projectId) {
        return call('getTestConversations', { projectId }, 'testConversations');
    },

    createTestConversation: function (projectId, name) {
        return call('createTestConversation', { projectId, name }, 'testConversation');
    },

    updateTestConversation: function (conversationId, name) {
        return call('updateTestConversation', { testConversationId: conversationId, name });
    },

    deleteTestConversation: function (conversationId) {
        return call('deleteTestConversation', { testConversationId: conversationId });
    },

    getTestPhrases: function (conversationId) {
        return call('getTestPhrases', { testConversationId: conversationId }, 'testPhrases');
    },

    updateTestPhrases: function (conversationId, updatedTestPhrases, deletedTestPhraseIds) {
        return call('updateTestPhrases', { testConversationId: conversationId, updatedTestPhrases, deletedTestPhraseIds });
    },

    reorderConversations: function (projectId, conversationIds) {
        return call('sortTestConversations', { projectId, testConversationIds: conversationIds });
    },

    getButtonOptions: function (projectId) {
        return call('getButtonOptions', { projectId }, 'options');
    },

    createButtonOptions: function (projectId, name, options) {
        return call('createButtonOptions', { projectId, name, options }, 'buttonOptions');
    },

    updateButtonOptions: function (projectId, id, name, options) {
        return call('updateButtonOptions', { projectId, id, name, options }, 'buttonOptions');
    },

    deleteButtonOptions: function (projectId, id) {
        return call('deleteButtonOptions', { projectId, id });
    },

    getUserBalanceInfo: function () {
        return call('getUserBalance', {}, null);
    },

    getStripePaymentIntent: function (amount) {
        return call('getStripePaymentIntent', { amount });
    },

    proceedStripePayment: function (stripePaymentIntentId, refund, nextPlanId) {
        return call('proceedStripePayment', { stripePaymentIntentId, refund, nextPlanId });
    },

    changePaymentMethod: function (planId, paymentType, paymentMethod) {
        return call('changePaymentMethod', { planId, paymentType, paymentMethod });
    },

    deletePaymentMethod: function (paymentMethodId) {
        return call('unlinkPaymentMethod', { paymentMethodId });
    },

    sendBillingToken: function (token) {
        return call('sendBillingToken', { token });
    },


    applyPromoCode: function (promoCode) {
        return call('applyPromo', { promoCode });
    },

    storePaymentAnalytics: function ({ isInitialBilling, paymentMethod, paymentAmount, paymentStatus, contentId, contentVersion }) {
        return call('storePaymentAnalytics', {
            isInitialBilling,
            paymentMethod,
            paymentAmount,
            paymentStatus,
            contentId,
            contentVersion,
            sessionId: getSessionId()
        });
    },

    storeUserAnalytics: function ({ action, ts, hex, data }) {
        return call('storeUserAnalytics', { action, ts, hex, data });
    },

    storePlanAnalytics: function ({ page, action, newPlanId, newPlanName, params }) {
        return call('storePlanAnalytics', {
            page,
            action,
            newPlanId,
            newPlanName,
            params,
            sessionId: getSessionId()
        });
    },

    storeInitPlanAnalytics: function ({ page, action, newPlanId, newPlanName, params, code }) {
        return call('storeInitPlanAnalytics', {
            page,
            action,
            newPlanId,
            newPlanName,
            params,
            currentPlanId: code,
            sessionId: getSessionId()
        });
    },

    getStudioConfig: function () {
        return call('getConfigParameter', { name: "var.studio.config" });
    },

    getVoicesConfig: function () {
        return call('getConfigParameter', { name: "var.voices.config" });
    },

    getPredefinedModelsConfig: function () {
        return call('getConfigParameter', { name: "var.wakeword.predefinedModels" });
    },

    getDefaultWakeWordOptionsConfig: function () {
        return call('getConfigParameter', { name: "var.wakeword.defaultWakeWordOptions" });
    },

    getWakeWordAcusticModelsConfig: function () {
        return call('getConfigParameter', { name: "var.wakeword.acousticModels" });
    },

    getSLUVersionsConfig: function () {
        return call('getSluVersions', {});
    },

    getPodLogString: function (podName) {
        return call('getPodLogString', { podName });
    },

    createPaypalOrder: function (amount) {
        return call('createPaypalOrder', { amount }, 'orderId');
    },

    checkIsPaypalOrderApproved: function (orderId) {
        return call('checkIsPaypalOrderApproved', { orderId }, 'isApproved');
    },

    getPaymentHistory: function () {
        return call('getPaymentHistory', {}, 'payments');
    },

    getGithubPromoInfo: function (encGithubToken) {
        return call('getGithubPromoInfo', { encGithubToken });
    },

    importProjectFiles: function (projectId, paths, importMode) {
        return call('importScripts', {
            projectId,
            paths,
            importMode,
        });
    },

    storeStudioData: function ({ projectId, deployment, key, value }) {
        return call('storeStudioData', {
            projectId,
            deployment,
            key,
            value,
        });
    },

    getStudioData: function ({ projectId, deployment, key }) {
        return call('getStudioData', {
            projectId,
            deployment,
            key,
        });
    },

    getUserPlans: function () {
        return new Promise(function (resolve, reject) {
            window.tutor.call('getUserPlans', {}, function (e, res) {
                if (e) {
                    reject({ error: e });
                } else if (res && res.error) {
                    reject(res);
                } else {
                    if (res && res.plans) {
                        for (let i = 0; i < res.plans.length; i++) {
                            res.plans[i].price = res.plans[i].price / 100;
                        }
                    }
                    resolve(res.plans);
                }
            });
        });
    },

    getPlans: function () {
        return new Promise(function (resolve, reject) {
            window.tutor.call('getPlans', {}, function (e, res) {
                if (e) {
                    reject({ error: e });
                } else if (res && res.error) {
                    reject(res);
                } else {
                    if (res && res.plans) {
                        for (let i = 0; i < res.plans.length; i++) {
                            res.plans[i].price = res.plans[i].price / 100;
                        }
                    }
                    resolve(res.plans);
                }
            });
        });
    },

    changePlan: function (newPlanId, paymentId, paymentType, onlyCheck) {
        return call('changePlan', { newPlanId, paymentId, paymentType, onlyCheck });
    },

    getUserPaymentMethods: function () {
        return call('getUserPaymentMethods', {}, 'cards');
    },

    verifyPlan: function (code) {
        return call('verifyPlan', { code });
    },

    resetNextPlanTransition: function () {
        return call('resetNextPlanTransition', {});
    },

    checkPlanStatus: function () {
        return call('checkPlanStatus', {});
    },

    cancelEnterpriseSubscription: function (planId) {
        return call('cancelEnterpriseSubscription', { planId });
    },

    resumeEnterpriseSubscription: function () {
        return call('resumeEnterpriseSubscription', {});
    },

    getTextToSpeach: function (text, voice) {
        return call('getTextToSpeechDuration', { text, voice });
    },

    getCohorts: function (projectId) {
        return call('getCohorts', { projectId });
    },

    createCohort: function (cohort) {
        return call('createCohort', { cohort });
    },
    updateCohort: function (cohort) {
        return call('updateCohort', { cohort });
    },
    deleteCohort: function (cohort) {
        return call('deleteCohort', { cohort });
    },
    searchLocationsForCohort: function (query) {
        return call('searchLocation', { query, limit: 10 });
    },
    getWakeWords: function (projectId) {
        return call('getWakewordOptions', { projectId });
    },
    createWakeWord: function (params) {
        return call('createWakewordOptions', params, 'wakewordOptions');
    },
    updateWakeWord: function (params) {
        return call('updateWakewordOptions', params, 'wakewordOptions');
    },
    deleteWakeWord: function (projectId, wakeWordId) {
        return call('deleteWakewordOptions', { projectId, id: wakeWordId });
    },
    getGitHubProjectSettings: function ({ projectId, deployId }) {
        return call('getGithubProjectSettings', { projectId, deployId });
    },
    saveGitHubProjectSettings: function (repoUrl, projectId, repoPermissions, deployId) {
        const params = {
            repoUrl, projectId, deployId
        };
        if (repoPermissions) {
            (params as any).repoPermissions = JSON.stringify(repoPermissions);
        }
        return call('saveGithubProjectSettings', params);
    },
    removeGitHubProjectSettings: function (projectId, deployId, version?) {
        return call('removeGithubProjectSettings', { projectId, deployId, version });
    },
    inviteFriend: function (email) {
        return call('inviteFriend', { email });
    },
    getInvitedFriendsInfo: function () {
        return call('getInvitedFriendsInfo', {});
    },
    getResourceUsage: function (param) {
        return call('getResourceUsage', param);
    },
    getCrawlerTasks: function (param) {
        return call('getCrawlerTasks', param);
    },
    restartCrawlerTask: function (param) {
        return call('restartCrawlerTask', param);
    },
    stopCrawlerTask: function (param) {
        return call('stopCrawlerTask', param);
    },
    deleteCrawlerTask: function (param) {
        return call('deleteCrawlerTask', param);
    },
};

function getFiltersFromReport(report) {
    return [
        {
            type: 'toggler',
            value: {
                name: 'period',
                value: report.period
            }
        },
        {
            type: 'timeRange',
            value: {
                name: 'dates',
                startTs: report.startTs,
                endTs: report.endTs
            }
        },
        {
            type: 'toggler',
            value: {
                name: 'onlyPatternOnPatternFlow',
                value: report.onlyPatternOnPatternFlow,
            }
        },
        ...report.filters
    ];
}

export default Api;
